import {useQueryClient} from '@tanstack/react-query';

import {CreateEventFormModal} from '../../../../component/events/event-form/create-event-form-modal';
import {CreatePromotionFormModal} from '../../../../component/promotions/promotion-form/create-promotion-form-modal';
import {CreateYandexStoryFormModal} from '../../../../component/stories/yandex-story-form/create-yandex-story-form-modal';
import {EVENTS_QUERY_KEY} from '../../../../service/events/events-hook';
import {PROMOTIONS_QUERY_KEY} from '../../../../service/promotions/promotions-hook';
import {STORIES_QUERY_KEY} from '../../../../service/yandex-stories/yandex-stories-hook';
import {YandexStoryTypeEnum} from '../../../../service/yandex-stories/yandex-stories-type';

// The three publication kinds that are created in a modal. A Post is absent on purpose: it has a
// dedicated page, so its callers navigate instead of opening a form here.
export enum PublicationFormEnum {
    Story = 'story',
    Promotion = 'promotion',
    Event = 'event',
}

const QUERY_KEY_BY_FORM: Record<PublicationFormEnum, string> = {
    [PublicationFormEnum.Story]: STORIES_QUERY_KEY,
    [PublicationFormEnum.Promotion]: PROMOTIONS_QUERY_KEY,
    [PublicationFormEnum.Event]: EVENTS_QUERY_KEY,
};

type PropsType = {
    form: PublicationFormEnum | null;
    onClose: () => void;
    onCreateSuccess?: () => void;
};

// Shared by the publication-list tab button and the calendar's create button, so the three form
// modals and the query keys they invalidate are declared in exactly one place.
export function PublicationCreateFormModal({form, onClose, onCreateSuccess}: PropsType): JSX.Element | null {
    const queryClient = useQueryClient();

    if (!form) {
        return null;
    }

    function handleCreateSuccess() {
        if (form) {
            queryClient.invalidateQueries([QUERY_KEY_BY_FORM[form]]);
        }

        onCreateSuccess?.();
    }

    // The modals take a `setIsOpen` setter; only a close needs handling here, and reacting to the
    // value rather than ignoring it keeps a future `setIsOpen(true)` from silently closing the form.
    function handleOpenChange(isOpen: boolean) {
        if (!isOpen) {
            onClose();
        }
    }

    if (form === PublicationFormEnum.Story) {
        return (
            <CreateYandexStoryFormModal
                onCreateSuccess={handleCreateSuccess}
                setIsOpen={handleOpenChange}
                type={YandexStoryTypeEnum.Story}
            />
        );
    }

    if (form === PublicationFormEnum.Promotion) {
        return <CreatePromotionFormModal onCreateSuccess={handleCreateSuccess} setIsOpen={handleOpenChange} />;
    }

    return <CreateEventFormModal onCreateSuccess={handleCreateSuccess} setIsOpen={handleOpenChange} />;
}
